import { isValidator, validateShape } from './validateShape'
import { ShapeFor } from './shapeFor'
import { ValidatorOrShape } from '../validators/types'

/**
 * Type guard function that checks if an object matches a given shape.
 * @template T - The type of object being validated
 * @param obj - The object to validate
 * @returns True if the object matches the shape, false otherwise
 */
export interface IsObjectOfShape<T extends object> {
  (obj: unknown): obj is T
}

/**
 * Creates a type guard function that checks if an object matches a given shape.
 * @template T - The type of object being validated
 * @param shape - The shape to validate against
 * @returns A type guard function
 */
export function createIsObjectOfShape<T extends object>(shape: ShapeFor<T>): IsObjectOfShape<T> {
  return (obj: unknown): obj is T => {
    return isObjectOfShape(obj, shape)
  }
}

/**
 * Recursively checks whether an object matches the provided shape.
 * This is a type guard function that can be used to narrow the type of an object.
 * 
 * @template T - The type of object being validated
 * @param obj - The object to validate
 * @param shape - The shape to validate against
 * @returns True if the object matches the shape, false otherwise
 * 
 * @remarks
 * - If any validator throws an AggregateValidationError, the object is not considered to match the shape, returning false
 * - If any validator throws any other error, it is immediately rethrown
 */
export const isObjectOfShape: <T extends object>(obj: unknown, shape: ShapeFor<T>) => obj is T = <T extends object>(obj: unknown, shape: ShapeFor<T>): obj is T => {
  if (isValidator(shape)) {
    // When shape is a Validator, validate the whole object as primitive
    return shape(obj).every(r => r.valid)
  }

  return validateShape(obj, shape as ValidatorOrShape, { soft: true }).success
}